import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/hooks/useI18n";
import { Check, X } from "lucide-react";
import { SUPPLIER_STATUS_VALUES, SupplierStatus } from "../models/activity.model";

interface SupplierStatusSelectProps {
  value?: SupplierStatus | null;
  /** `null` riporta l'attività allo stato iniziale, senza stato fornitore. */
  onChange: (value: SupplierStatus | null) => void;
  disabled?: boolean;
}

/**
 * Radix non accetta un SelectItem con valore stringa vuota: la voce che svuota
 * il campo viaggia con un valore sentinella, tradotto in `null` qui dentro.
 */
const CLEAR_VALUE = '__none__';

/**
 * Selettore dello stato di prenotazione presso il fornitore per una singola attività.
 * Uso interno del Travel Advisor: reso dal chiamante in qualsiasi stato dell'itinerario,
 * purché l'utente non sia un traveler e la vista non sia read-only. Il cliente non lo vede mai.
 */
const STATUS_STYLES: Record<SupplierStatus, string> = {
  contacted: 'bg-gray-100 text-gray-700 border-gray-300',
  optioned: 'bg-sky-100 text-sky-700 border-sky-300',
  confirmed: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  partially_paid: 'bg-amber-100 text-amber-700 border-amber-300',
  paid: 'bg-green-100 text-green-700 border-green-300',
};

export const SupplierStatusSelect = ({ value, onChange, disabled }: SupplierStatusSelectProps) => {
  const { t } = useI18n();

  return (
    <Select
      value={value ?? undefined}
      onValueChange={(v) => onChange(v === CLEAR_VALUE ? null : (v as SupplierStatus))}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={t('builder.supplierStatus.label')}
        className={`h-8 w-auto shrink-0 gap-2 rounded-full border px-3 text-xs font-medium ${
          value ? STATUS_STYLES[value] : 'bg-white text-gray-500 border-gray-300'
        }`}
      >
        {value === 'paid' && <Check className="h-3.5 w-3.5" />}
        <SelectValue placeholder={t('builder.supplierStatus.placeholder')} />
      </SelectTrigger>
      <SelectContent>
        {/* Prima voce: torna alla situazione iniziale dopo una scelta sbagliata. */}
        {value && (
          <SelectItem value={CLEAR_VALUE} className="text-gray-500">
            <span className="flex items-center gap-2">
              <X className="h-3.5 w-3.5" />
              {t('builder.supplierStatus.clear')}
            </span>
          </SelectItem>
        )}
        {SUPPLIER_STATUS_VALUES.map((status) => (
          <SelectItem key={status} value={status}>
            {t(`builder.supplierStatus.options.${status}` as Parameters<typeof t>[0])}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
